/*!
 * Rhinokey Scratch-to-Reveal Popup
 * Full-screen takeover replicating the recorded Aiva Pop Up flow:
 * scratch (no data required) -> reveal prize inline -> email capture ->
 * phone/SMS capture (skippable) -> success + CTA to the offer page.
 *
 * No backend, no OAuth app required. Configure via `window.RhinokeyPopupConfig`
 * before this script loads, or edit DEFAULTS below. See README.md.
 */
(function () {
  'use strict';

  var DEFAULTS = {
    enabled: true,

    // --- Trigger rules. If apiBaseUrl is set, enabled/delaySeconds/exitIntent/
    // frequencyMode/frequencyDays/showOnPaths/hideOnPaths are overridden at runtime
    // by whatever is set on the app's Popup Settings page — these are just the
    // fallback/offline values. ---
    delaySeconds: 8,
    exitIntent: true,
    scrollPercent: null,
    // ONCE_PER_VISITOR | EVERY_N_DAYS | ALWAYS
    frequencyMode: 'EVERY_N_DAYS',
    frequencyDays: 7,
    // showOnPaths: exact-match allowlist, null/empty = show anywhere (subject to hideOnPaths).
    // hideOnPaths: prefix-match blocklist, e.g. '/checkout' also excludes '/checkout/thank-you'.
    showOnPaths: null,
    hideOnPaths: ['/checkout', '/cart'],

    // --- Brand ---
    logoText: 'rhinokey',

    // --- Teaser tab: stays pinned to the corner once the main popup has been
    // shown/suppressed or closed, so visitors can still open it manually.
    // Overridden at runtime by the Popup Settings page when apiBaseUrl is set. ---
    teaserEnabled: true,
    teaserText: '🎁 Get My Discount',
    // 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center-right' | 'center-left'
    teaserPosition: 'bottom-right',
    teaserColor: '#2ecc82',

    // --- Background is always the built-in dark gradient + pattern. Optionally layer a
    // transparent product cutout (PNG/WebP) on top of it on the right side. ---
    productImage: {
      src: null
    },

    // --- Step 1: scratch ---
    scratchHeadline: 'TRY YOUR LUCK',
    scratchSubheadline: 'Scratch below to see what you get',
    scratchHint: 'SCRATCH HERE',
    claimButtonLabel: 'Claim Your Reward',

    // --- The single prize this popup always awards ---
    prize: {
      name: 'Free Smartcard',
      // Copy for the email step headline, e.g. "Free" + accent-italic "Smartcard"
      emailHeadlinePlain: 'Free',
      emailHeadlineAccent: 'Smartcard',
      emailSubheadline: 'Leave your email below to unlock a Free Smartcard + Member only extras.',
      emailButtonLabel: 'Claim Free Smartcard',
      phoneButtonLabel: 'Get Free Smartcard',
      offerText: 'Buy 2 Get 1 Free Offer',
      successHeadline: 'Success!',
      successCtaLabel: 'Shop Smartcard',
      redirectUrl: 'https://www.rhinokey.com/products/rhinokey-smartcard'
    },

    // --- Step 1.5: qualifying question, shown right after the scratch reveal
    // and before the email form (same pattern competitors like Spotminders
    // use — an easy tap-to-answer question raises form completion before
    // asking for contact info). The chosen answer rides along on the
    // EMAIL_CAPTURED/PHONE_CAPTURED events and gets synced to Klaviyo as a
    // "tracking_interest" custom property when apiBaseUrl is set. ---
    collectInterest: true,
    interestHeadline: 'What Are You Looking To Track?',
    interestOptions: [
      { label: 'Wallet' },
      { label: 'Luggage' },
      { label: 'Keys' },
      { label: 'Bag' },
      { label: "I'm looking for a gift", wide: true },
      { label: 'Something else', wide: true }
    ],

    // --- Step 2: email ---
    emailPlaceholder: 'Enter your email',

    // --- Step 3: phone / SMS opt-in ---
    collectPhone: true,
    phoneHeadline: 'Finish Signing Up',
    phoneSubheadline: 'for texts to become a VIP',
    phoneLegalText: 'By providing your number and clicking the button, you agree to receive recurring auto-dialed marketing SMS (including cart reminders). Msg content, artificial or prerecorded voice and our Terms of Service (including arbitration). Consent is not required to purchase. Msg & data rates may apply. Msg frequency varies. Reply HELP for help, STOP to opt-out. <a href="https://www.rhinokey.com/policies/privacy-policy" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;">View Privacy Policy</a>.',
    phonePlaceholder: 'Your phone number',
    skipStepLabel: 'Skip This Step',
    defaultDialIso: 'US',

    // --- Step 4: success ---
    successSubPrefix: "You've unlocked our",
    successSubSuffix: 'Click the button below to claim the offer.',

    // --- Optional: subscribe email/phone directly to Klaviyo (client-side, public key only).
    // Ignored if apiBaseUrl is set — the backend handles Klaviyo sync in that case. ---
    klaviyo: {
      enabled: false,
      publicApiKey: null,
      listId: null
    },

    // --- Optional: the deployed rhinokey-insights backend (see /rhinokey-insights).
    // When set, view/scratch/email/phone/CTA events are reported there for the
    // analytics dashboard, and Klaviyo sync happens server-side instead of via
    // the klaviyo.* config above. Leave null to run in standalone mode. ---
    apiBaseUrl: null
  };

  function deepMerge(base, override) {
    if (!override) return base;
    var out = Array.isArray(base) ? base.slice() : Object.assign({}, base);
    for (var key in override) {
      if (!Object.prototype.hasOwnProperty.call(override, key)) continue;
      var val = override[key];
      if (val && typeof val === 'object' && !Array.isArray(val) && base[key] && typeof base[key] === 'object') {
        out[key] = deepMerge(base[key], val);
      } else {
        out[key] = val;
      }
    }
    return out;
  }

  var CONFIG = deepMerge(DEFAULTS, window.RhinokeyPopupConfig || {});

  var STORAGE_KEY = 'rk_scratch_popup_state_v2';

  function readState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function writeState(patch) {
    var state = deepMerge(readState(), patch);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
    return state;
  }
  function shouldSuppress() {
    var state = readState();
    if (!state.lastShownAt) return false;
    if (CONFIG.frequencyMode === 'ALWAYS') return false;
    if (CONFIG.frequencyMode === 'ONCE_PER_VISITOR') return true;
    // EVERY_N_DAYS
    if (!CONFIG.frequencyDays || CONFIG.frequencyDays <= 0) return false;
    return (Date.now() - state.lastShownAt) / 86400000 < CONFIG.frequencyDays;
  }
  function pathAllowed() {
    var path = window.location.pathname;
    if (CONFIG.hideOnPaths && CONFIG.hideOnPaths.some(function (p) { return path.indexOf(p) === 0; })) return false;
    if (CONFIG.showOnPaths && CONFIG.showOnPaths.length) return CONFIG.showOnPaths.indexOf(path) !== -1;
    return true;
  }
  function isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); }

  // ---------- Analytics reporting (rhinokey-insights backend, optional) ----------
  var VISITOR_ID_KEY = 'rk_visitor_id';

  function getVisitorId() {
    try {
      var id = localStorage.getItem(VISITOR_ID_KEY);
      if (!id) {
        id = 'v_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        localStorage.setItem(VISITOR_ID_KEY, id);
      }
      return id;
    } catch (e) {
      return 'v_' + Date.now().toString(36);
    }
  }

  function reportEvent(eventType, extra) {
    if (!CONFIG.apiBaseUrl) return;
    var shop = (window.Shopify && window.Shopify.shop) || window.location.hostname;
    var payload = Object.assign({
      shop: shop,
      visitorId: getVisitorId(),
      eventType: eventType,
      path: window.location.pathname
    }, extra || {});

    try {
      fetch(CONFIG.apiBaseUrl.replace(/\/$/, '') + '/api/popup-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* no-op */ }
  }

  // ---------- Dial code data (common countries; extend freely) ----------
  var COUNTRIES = [
    { iso: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
    { iso: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
    { iso: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
    { iso: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
    { iso: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
    { iso: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
    { iso: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
    { iso: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹' },
    { iso: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱' },
    { iso: 'BE', name: 'Belgium', dial: '+32', flag: '🇧🇪' },
    { iso: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭' },
    { iso: 'IE', name: 'Ireland', dial: '+353', flag: '🇮🇪' },
    { iso: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
    { iso: 'SE', name: 'Sweden', dial: '+46', flag: '🇸🇪' },
    { iso: 'NO', name: 'Norway', dial: '+47', flag: '🇳🇴' },
    { iso: 'DK', name: 'Denmark', dial: '+45', flag: '🇩🇰' },
    { iso: 'FI', name: 'Finland', dial: '+358', flag: '🇫🇮' },
    { iso: 'PL', name: 'Poland', dial: '+48', flag: '🇵🇱' },
    { iso: 'AT', name: 'Austria', dial: '+43', flag: '🇦🇹' },
    { iso: 'GR', name: 'Greece', dial: '+30', flag: '🇬🇷' },
    { iso: 'MA', name: 'Morocco', dial: '+212', flag: '🇲🇦' },
    { iso: 'DZ', name: 'Algeria', dial: '+213', flag: '🇩🇿' },
    { iso: 'TN', name: 'Tunisia', dial: '+216', flag: '🇹🇳' },
    { iso: 'EG', name: 'Egypt', dial: '+20', flag: '🇪🇬' },
    { iso: 'AE', name: 'United Arab Emirates', dial: '+971', flag: '🇦🇪' },
    { iso: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '🇸🇦' },
    { iso: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦' },
    { iso: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬' },
    { iso: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
    { iso: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰' },
    { iso: 'CN', name: 'China', dial: '+86', flag: '🇨🇳' },
    { iso: 'JP', name: 'Japan', dial: '+81', flag: '🇯🇵' },
    { iso: 'KR', name: 'South Korea', dial: '+82', flag: '🇰🇷' },
    { iso: 'SG', name: 'Singapore', dial: '+65', flag: '🇸🇬' },
    { iso: 'MY', name: 'Malaysia', dial: '+60', flag: '🇲🇾' },
    { iso: 'PH', name: 'Philippines', dial: '+63', flag: '🇵🇭' },
    { iso: 'ID', name: 'Indonesia', dial: '+62', flag: '🇮🇩' },
    { iso: 'TH', name: 'Thailand', dial: '+66', flag: '🇹🇭' },
    { iso: 'VN', name: 'Vietnam', dial: '+84', flag: '🇻🇳' },
    { iso: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽' },
    { iso: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷' },
    { iso: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
    { iso: 'CL', name: 'Chile', dial: '+56', flag: '🇨🇱' },
    { iso: 'CO', name: 'Colombia', dial: '+57', flag: '🇨🇴' },
    { iso: 'NZ', name: 'New Zealand', dial: '+64', flag: '🇳🇿' },
    { iso: 'TR', name: 'Turkey', dial: '+90', flag: '🇹🇷' },
    { iso: 'IL', name: 'Israel', dial: '+972', flag: '🇮🇱' },
    { iso: 'RU', name: 'Russia', dial: '+7', flag: '🇷🇺' },
    { iso: 'UA', name: 'Ukraine', dial: '+380', flag: '🇺🇦' }
  ];

  function findCountry(iso) {
    for (var i = 0; i < COUNTRIES.length; i++) if (COUNTRIES[i].iso === iso) return COUNTRIES[i];
    return COUNTRIES[0];
  }

  // ---------- Klaviyo (optional, client-side, public key only) ----------
  function klaviyoSubscribe(profileAttrs) {
    var k = CONFIG.klaviyo;
    if (!k || !k.enabled || !k.publicApiKey || !k.listId) return;
    try {
      fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(k.publicApiKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: profileAttrs } } },
            relationships: { list: { data: { type: 'list', id: k.listId } } }
          }
        })
      }).catch(function () {});
    } catch (e) {}
  }

  // ---------- Confetti (success step) ----------
  function burstConfetti(canvas) {
    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    var colors = ['#2ecc82', '#1fa869', '#ffffff', '#eafff2'];
    var count = 140;
    var particles = [];
    for (var i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: -20 - Math.random() * h * 0.4,
        vx: (Math.random() - 0.5) * 1.8,
        vy: 2 + Math.random() * 2.5,
        size: 5 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.25,
        gravity: 0.03 + Math.random() * 0.04
      });
    }

    var start = null;
    var duration = 2600;
    function frame(ts) {
      if (!start) start = ts;
      var elapsed = ts - start;
      ctx.clearRect(0, 0, w, h);
      particles.forEach(function (p) {
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - elapsed / duration);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });
      if (elapsed < duration) {
        requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    }
    requestAnimationFrame(frame);
  }

  // ---------- Build DOM ----------
  function buildPopup() {
    var overlay = document.createElement('div');
    overlay.className = 'rk-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    // Background is always the built-in dark gradient + hairline pattern. If a transparent
    // product cutout is configured, it's layered on top on the right side; otherwise a
    // dashed placeholder box shows where it will go.
    var bgHtml = '<div class="rk-bg rk-bg-pattern"><div class="rk-bg-scrim"></div></div>';

    var visualHtml;
    if (CONFIG.productImage.src) {
      visualHtml = '<div class="rk-visual"><img class="rk-visual-img" src="' + CONFIG.productImage.src + '" alt="" /></div>';
    } else {
      visualHtml = '<div class="rk-visual"><div class="rk-visual-box">' +
          '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="9" r="2" stroke="currentColor" stroke-width="1.5"/><path d="M2 17l5.5-5.5a2 2 0 0 1 2.83 0L15 16.17M13 14l2.5-2.5a2 2 0 0 1 2.83 0L22 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '<div class="rk-visual-label">Product image</div>' +
        '</div></div>';
    }
    bgHtml += visualHtml;

    var interestOptionsHtml = CONFIG.interestOptions.map(function (opt) {
      return '<button type="button" class="rk-interest-option' + (opt.wide ? ' rk-interest-option--wide' : '') + '" data-value="' + opt.label + '">' + opt.label + '</button>';
    }).join('');

    overlay.innerHTML =
      bgHtml +
      '<button type="button" class="rk-close" aria-label="Close">&times;</button>' +
      '<div class="rk-content"><div class="rk-content-inner">' +
        '<div class="rk-logo">' + CONFIG.logoText + '<sup>&reg;</sup></div>' +

        '<div class="rk-step rk-active" data-step="scratch">' +
          '<h2 class="rk-headline">' + CONFIG.scratchHeadline + '</h2>' +
          '<p class="rk-subheadline">' + CONFIG.scratchSubheadline + '</p>' +
          '<div class="rk-scratch-box">' +
            '<div class="rk-prize-layer">' +
              '<div class="rk-prize-you-got">YOU\'VE GOT</div>' +
              '<div class="rk-prize-name">' + CONFIG.prize.name + '</div>' +
            '</div>' +
            '<canvas class="rk-scratch-canvas"></canvas>' +
            '<div class="rk-scratch-hint">' + CONFIG.scratchHint + '</div>' +
          '</div>' +
          '<div class="rk-claim-wrap"><button type="button" class="rk-btn rk-claim-btn">' + CONFIG.claimButtonLabel + '</button></div>' +
        '</div>' +

        '<div class="rk-step" data-step="interest">' +
          '<h2 class="rk-headline rk-headline--sm">' + CONFIG.interestHeadline + '</h2>' +
          '<div class="rk-interest-options">' + interestOptionsHtml + '</div>' +
        '</div>' +

        '<div class="rk-step" data-step="email">' +
          '<h2 class="rk-headline rk-headline-mixed">' + CONFIG.prize.emailHeadlinePlain + ' <span class="rk-accent-italic">' + CONFIG.prize.emailHeadlineAccent + '</span></h2>' +
          '<p class="rk-subheadline">' + CONFIG.prize.emailSubheadline + '</p>' +
          '<form class="rk-email-form">' +
            '<input type="email" class="rk-input rk-email-input" placeholder="' + CONFIG.emailPlaceholder + '" required autocomplete="email" />' +
            '<div class="rk-error rk-email-error"></div>' +
            '<button type="submit" class="rk-btn">' + CONFIG.prize.emailButtonLabel + '</button>' +
          '</form>' +
        '</div>' +

        '<div class="rk-step" data-step="phone">' +
          '<h2 class="rk-headline">' + CONFIG.phoneHeadline + '</h2>' +
          '<p class="rk-subheadline">' + CONFIG.phoneSubheadline + '</p>' +
          '<p class="rk-legal">' + CONFIG.phoneLegalText + '</p>' +
          '<form class="rk-phone-form">' +
            '<div class="rk-phone-row">' +
              '<button type="button" class="rk-dial-btn"><img class="rk-flag-img" alt="" /><span class="rk-dial-code-label"></span><span class="rk-caret">&#9660;</span></button>' +
              '<input type="tel" class="rk-phone-input" placeholder="' + CONFIG.phonePlaceholder + '" />' +
              '<div class="rk-dial-dropdown">' +
                '<input type="text" class="rk-dial-search" placeholder="Search country" />' +
                '<div class="rk-dial-options"></div>' +
              '</div>' +
            '</div>' +
            '<div class="rk-error rk-phone-error"></div>' +
            '<button type="submit" class="rk-btn">' + CONFIG.prize.phoneButtonLabel + '</button>' +
          '</form>' +
          '<button type="button" class="rk-skip-link">' + CONFIG.skipStepLabel + '</button>' +
        '</div>' +

        '<div class="rk-step" data-step="success">' +
          '<h2 class="rk-headline">' + CONFIG.prize.successHeadline + '</h2>' +
          '<p class="rk-subheadline">' + CONFIG.successSubPrefix + ' <span class="rk-success-offer">' + CONFIG.prize.offerText + '</span>. ' + CONFIG.successSubSuffix + '</p>' +
          '<button type="button" class="rk-btn rk-success-cta">' + CONFIG.prize.successCtaLabel + '</button>' +
        '</div>' +

      '</div></div>' +
      '<canvas class="rk-confetti-canvas"></canvas>';

    document.body.appendChild(overlay);
    return overlay;
  }

  // ---------- Scratch canvas logic ----------
  function setupScratchCanvas(overlay, onRevealed) {
    var box = overlay.querySelector('.rk-scratch-box');
    var canvas = overlay.querySelector('.rk-scratch-canvas');
    var hint = overlay.querySelector('.rk-scratch-hint');
    var ctx = canvas.getContext('2d');
    var revealed = false;

    function drawSurface(w, h) {
      var grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, '#a8a8a8');
      grad.addColorStop(0.5, '#dcdcdc');
      grad.addColorStop(1, '#a8a8a8');
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      for (var i = 0; i < w; i += 4) ctx.fillRect(i, 0, 1, h);
    }

    function size() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = box.clientWidth, h = box.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawSurface(w, h);
    }
    size();
    window.addEventListener('resize', size);

    var drawing = false;
    var lastPoint = null;

    function getPoint(e) {
      var rect = canvas.getBoundingClientRect();
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    }

    function scratchAt(point) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineWidth = 28;
      ctx.beginPath();
      if (lastPoint) {
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(point.x, point.y);
      } else {
        ctx.moveTo(point.x, point.y);
        ctx.lineTo(point.x + 0.1, point.y + 0.1);
      }
      ctx.stroke();
      lastPoint = point;
    }

    function revealedPercent() {
      var w = canvas.width, h = canvas.height, data;
      try { data = ctx.getImageData(0, 0, w, h).data; } catch (e) { return 0; }
      var transparent = 0, total = 0;
      for (var i = 3; i < data.length; i += 4 * 8) { total++; if (data[i] < 80) transparent++; }
      return total ? transparent / total : 0;
    }

    function maybeAutoReveal() {
      if (revealed) return;
      if (revealedPercent() > 0.5) {
        revealed = true;
        ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
        onRevealed();
      }
    }

    function start(e) { drawing = true; hint.style.display = 'none'; lastPoint = null; scratchAt(getPoint(e)); }
    function move(e) { if (!drawing) return; e.preventDefault(); scratchAt(getPoint(e)); maybeAutoReveal(); }
    function end() { drawing = false; lastPoint = null; maybeAutoReveal(); }

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: true });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
  }

  // ---------- Phone dial dropdown ----------
  function setupDialDropdown(overlay) {
    var dialBtn = overlay.querySelector('.rk-dial-btn');
    var dropdown = overlay.querySelector('.rk-dial-dropdown');
    var search = overlay.querySelector('.rk-dial-search');
    var optionsWrap = overlay.querySelector('.rk-dial-options');
    var flagEl = overlay.querySelector('.rk-dial-btn .rk-flag-img');
    var labelEl = overlay.querySelector('.rk-dial-code-label');

    var selected = findCountry(CONFIG.defaultDialIso);

    // Emoji flags render inconsistently across browsers/OSes (notably Windows),
    // so flags are rendered as small images from flagcdn.com instead.
    function flagUrl(iso, size) {
      return 'https://flagcdn.com/' + (size || '24x18') + '/' + iso.toLowerCase() + '.png';
    }

    function applySelected() {
      flagEl.src = flagUrl(selected.iso);
      flagEl.alt = selected.name;
      labelEl.textContent = selected.dial;
    }
    applySelected();

    function renderOptions(filter) {
      var f = (filter || '').toLowerCase();
      optionsWrap.innerHTML = '';
      COUNTRIES.filter(function (c) {
        return !f || c.name.toLowerCase().indexOf(f) === 0 || c.name.toLowerCase().indexOf(f) !== -1;
      }).forEach(function (c) {
        var row = document.createElement('div');
        row.className = 'rk-dial-option';
        row.innerHTML = '<img class="rk-flag-img" src="' + flagUrl(c.iso, '16x12') + '" alt="" /><span>' + c.name + '</span><span class="rk-dial-code">' + c.dial + '</span>';
        row.addEventListener('click', function () {
          selected = c;
          applySelected();
          dropdown.classList.remove('rk-open');
        });
        optionsWrap.appendChild(row);
      });
    }
    renderOptions('');

    dialBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      dropdown.classList.toggle('rk-open');
      if (dropdown.classList.contains('rk-open')) {
        search.value = '';
        renderOptions('');
        search.focus();
      }
    });
    search.addEventListener('input', function () { renderOptions(search.value); });
    document.addEventListener('click', function (e) {
      if (!dropdown.contains(e.target) && e.target !== dialBtn) dropdown.classList.remove('rk-open');
    });

    return { getSelected: function () { return selected; } };
  }

  // ---------- Teaser tab ----------
  var TEASER_POSITIONS = ['bottom-right', 'bottom-left', 'top-right', 'top-left', 'center-right', 'center-left'];

  function hexToRgbTuple(hex) {
    var match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    var normalized = match ? match[1] : '2ecc82';
    return [
      parseInt(normalized.slice(0, 2), 16),
      parseInt(normalized.slice(2, 4), 16),
      parseInt(normalized.slice(4, 6), 16)
    ];
  }

  function rgbToHex(rgb) {
    return '#' + rgb.map(function (channel) {
      return Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0');
    }).join('');
  }

  function shadeColor(hex, percent) {
    var rgb = hexToRgbTuple(hex);
    return rgbToHex(rgb.map(function (channel) { return channel * (1 + percent); }));
  }

  // Relative luminance (WCAG) to pick readable black/white text on any background.
  function readableTextColor(hex) {
    var rgb = hexToRgbTuple(hex).map(function (channel) {
      var c = channel / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    var luminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    return luminance > 0.5 ? '#06170f' : '#ffffff';
  }

  function buildTeaser() {
    var existing = document.querySelector('.rk-teaser');
    if (existing) return existing;

    var wrap = document.createElement('div');
    wrap.className = 'rk-teaser';

    var openBtn = document.createElement('button');
    openBtn.type = 'button';
    openBtn.className = 'rk-teaser-open';
    openBtn.addEventListener('click', function () {
      hideTeaser();
      openPopup();
    });

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'rk-teaser-close';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      dismissTeaser();
    });

    wrap.appendChild(openBtn);
    wrap.appendChild(closeBtn);
    document.body.appendChild(wrap);
    return wrap;
  }

  function styleTeaser(wrap) {
    var position = TEASER_POSITIONS.indexOf(CONFIG.teaserPosition) !== -1 ? CONFIG.teaserPosition : 'bottom-right';
    TEASER_POSITIONS.forEach(function (pos) { wrap.classList.remove('rk-teaser--' + pos); });
    wrap.classList.add('rk-teaser--' + position);

    var color = /^#[0-9a-f]{6}$/i.test(CONFIG.teaserColor || '') ? CONFIG.teaserColor : '#2ecc82';
    wrap.style.setProperty('--rk-teaser-bg', color);
    wrap.style.setProperty('--rk-teaser-bg-hover', shadeColor(color, -0.18));
    wrap.style.setProperty('--rk-teaser-fg', readableTextColor(color));
  }

  function showTeaser() {
    if (!CONFIG.enabled || !CONFIG.teaserEnabled) return;
    var state = readState();
    if (state.converted || state.teaserDismissed) return;
    if (document.querySelector('.rk-overlay')) return;
    var wrap = buildTeaser();
    wrap.querySelector('.rk-teaser-open').textContent = CONFIG.teaserText;
    styleTeaser(wrap);
    requestAnimationFrame(function () { wrap.classList.add('rk-teaser-visible'); });
  }

  function hideTeaser() {
    var wrap = document.querySelector('.rk-teaser');
    if (wrap) wrap.classList.remove('rk-teaser-visible');
  }

  // Visitor explicitly closed the teaser via its × — respect that for good,
  // same as the "already converted" rule, instead of re-showing it later.
  function dismissTeaser() {
    writeState({ teaserDismissed: true });
    hideTeaser();
    var wrap = document.querySelector('.rk-teaser');
    if (wrap) setTimeout(function () { wrap.remove(); }, 300);
  }

  function maybeShowTeaser() {
    // Stond op 500ms na het laden, waardoor de teaser de thumbnails afdekte in de
    // eerste schermvulling. Nu pas tonen zodra de bezoeker voorbij de vouw scrolt,
    // met een ruime tijdslimiet als terugval voor wie blijft hangen.
    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      window.removeEventListener('scroll', onScroll);
      showTeaser();
    }
    function onScroll() {
      if (window.scrollY > window.innerHeight * 0.6) reveal();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(reveal, 25000);
    onScroll();
  }

  // ---------- Main flow ----------
  function openPopup() {
    if (document.querySelector('.rk-overlay')) return;
    hideTeaser();

    var overlay = buildPopup();

    requestAnimationFrame(function () { overlay.classList.add('rk-visible'); });
    writeState({ lastShownAt: Date.now() });
    document.body.style.overflow = 'hidden';
    reportEvent('VIEW');

    function goToStep(name) {
      overlay.querySelectorAll('.rk-step').forEach(function (el) {
        el.classList.toggle('rk-active', el.getAttribute('data-step') === name);
      });
    }

    function revealSuccess() {
      goToStep('success');
      burstConfetti(overlay.querySelector('.rk-confetti-canvas'));
    }

    function closePopup() {
      overlay.classList.remove('rk-visible');
      document.body.style.overflow = '';
      setTimeout(function () { overlay.remove(); }, 250);
      writeState({ lastShownAt: Date.now() });
      maybeShowTeaser();
    }

    overlay.querySelector('.rk-close').addEventListener('click', closePopup);
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape' && document.body.contains(overlay)) {
        closePopup();
        document.removeEventListener('keydown', escHandler);
      }
    });

    // Step 1: scratch (no data required)
    setupScratchCanvas(overlay, function () {
      overlay.querySelector('.rk-claim-wrap').classList.add('rk-show');
      reportEvent('SCRATCH_REVEAL');
    });
    overlay.querySelector('.rk-claim-btn').addEventListener('click', function () {
      goToStep(CONFIG.collectInterest ? 'interest' : 'email');
    });

    // Step 1.5: interest (optional qualifying question)
    if (CONFIG.collectInterest) {
      overlay.querySelectorAll('.rk-interest-option').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var value = btn.getAttribute('data-value');
          writeState({ interest: value });
          reportEvent('INTEREST_SELECTED', { interest: value });
          goToStep('email');
        });
      });
    }

    // Step 2: email
    var emailForm = overlay.querySelector('.rk-email-form');
    emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = emailForm.querySelector('.rk-email-input');
      var errorEl = emailForm.querySelector('.rk-email-error');
      var email = input.value.trim();
      if (!isValidEmail(email)) {
        errorEl.textContent = 'Please enter a valid email address.';
        input.focus();
        return;
      }
      errorEl.textContent = '';
      var interest = readState().interest;
      if (!CONFIG.apiBaseUrl) {
        klaviyoSubscribe(interest ? { email: email, properties: { tracking_interest: interest } } : { email: email });
      }
      reportEvent('EMAIL_CAPTURED', { email: email, interest: interest });
      writeState({ email: email });

      if (CONFIG.collectPhone) {
        goToStep('phone');
      } else {
        revealSuccess();
      }
    });

    // Step 3: phone (optional, skippable)
    if (CONFIG.collectPhone) {
      var dial = setupDialDropdown(overlay);
      var phoneForm = overlay.querySelector('.rk-phone-form');
      phoneForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = phoneForm.querySelector('.rk-phone-input');
        var errorEl = phoneForm.querySelector('.rk-phone-error');
        var digits = input.value.replace(/[^0-9]/g, '');
        if (digits.length < 7 || digits.length > 15) {
          errorEl.textContent = 'Invalid phone number.';
          input.focus();
          return;
        }
        errorEl.textContent = '';
        var fullPhone = dial.getSelected().dial + digits;
        var phoneInterest = readState().interest;
        if (!CONFIG.apiBaseUrl) {
          klaviyoSubscribe(phoneInterest ? { phone_number: fullPhone, properties: { tracking_interest: phoneInterest } } : { phone_number: fullPhone });
        }
        // Include the email/interest captured earlier so the backend attaches this
        // phone number to the same Klaviyo profile instead of creating an unmatched one.
        reportEvent('PHONE_CAPTURED', { phone: fullPhone, email: readState().email, interest: phoneInterest });
        writeState({ phone: fullPhone });
        revealSuccess();
      });
      overlay.querySelector('.rk-skip-link').addEventListener('click', function () {
        reportEvent('PHONE_SKIPPED');
        revealSuccess();
      });
    }

    // Step 4: success
    overlay.querySelector('.rk-success-cta').addEventListener('click', function () {
      writeState({ converted: true });
      reportEvent('CTA_CLICK');
      window.location.href = CONFIG.prize.redirectUrl;
    });
  }

  // ---------- Trigger scheduling ----------
  function initTriggers() {
    if (!CONFIG.enabled || !pathAllowed()) return;

    if (shouldSuppress()) {
      maybeShowTeaser();
      return;
    }

    var opened = false;
    function triggerOnce() {
      if (opened) return;
      opened = true;
      openPopup();
    }

    if (CONFIG.delaySeconds != null) setTimeout(triggerOnce, CONFIG.delaySeconds * 1000);

    if (CONFIG.exitIntent) {
      document.addEventListener('mouseout', function (e) {
        if (!e.relatedTarget && !e.toElement && e.clientY <= 0) triggerOnce();
      });
    }

    if (CONFIG.scrollPercent != null) {
      window.addEventListener('scroll', function () {
        var scrolled = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        if (scrolled >= CONFIG.scrollPercent) triggerOnce();
      });
    }

    window.RhinokeyPopup = { open: openPopup, reset: function () { localStorage.removeItem(STORAGE_KEY); } };
  }

  // Pulls enabled/delaySeconds/exitIntent/frequency* from the app's Popup Settings
  // page so changes there take effect without a theme edit. Falls back to the
  // local CONFIG values (unchanged) if apiBaseUrl isn't set, the request fails,
  // or it doesn't respond within the timeout — the popup should never silently
  // stop working because the backend is briefly unreachable.
  function fetchRemoteConfig(callback) {
    if (!CONFIG.apiBaseUrl) return callback();

    var shop = (window.Shopify && window.Shopify.shop) || window.location.hostname;
    var url = CONFIG.apiBaseUrl.replace(/\/$/, '') + '/api/popup-config?shop=' + encodeURIComponent(shop);
    var done = false;
    function finish(remote) {
      if (done) return;
      done = true;
      if (remote && remote.ok) {
        CONFIG = deepMerge(CONFIG, {
          enabled: remote.enabled,
          delaySeconds: remote.delaySeconds,
          exitIntent: remote.exitIntent,
          scrollPercent: remote.scrollPercent,
          frequencyMode: remote.frequencyMode,
          frequencyDays: remote.frequencyDays,
          teaserEnabled: remote.teaserEnabled,
          teaserText: remote.teaserText,
          teaserPosition: remote.teaserPosition,
          teaserColor: remote.teaserColor,
          showOnPaths: remote.showOnPaths,
          hideOnPaths: remote.hideOnPaths,
          prize: {
            successCtaLabel: remote.successCtaText,
            redirectUrl: remote.successCtaUrl
          }
        });
      }
      callback();
    }

    setTimeout(function () { finish(null); }, 2500);
    fetch(url).then(function (res) { return res.json(); }).then(finish).catch(function () { finish(null); });
  }

  function boot() {
    fetchRemoteConfig(initTriggers);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
